import './style.scss';

import { forwardRef, useImperativeHandle, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from 'react-query';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { yupResolver } from '@hookform/resolvers/yup';

import { Accordion, AccordionTab } from 'primereact/accordion';
import {
  downloadFile,
  getReviseLogs,
  requestUpdate,
  updateUpdateRequest,
} from 'apis/projectProposals.api';
import { createRequestUpdateValidationSchema } from 'features/ReviseProjectProposal/validation';
import { FileInput, TextareaInput } from 'components/FormControl';
import Loading from 'components/Loading';
import { TIMEZONE_VN, TOAST } from 'constant';
import { toast } from 'layout';
import { checkOverdueDatetime, formatFileName, getMaxDate, mapError } from 'utils/func';

const formOptions = { resolver: yupResolver(createRequestUpdateValidationSchema) };

const RequestUpdateForm = forwardRef((props, ref) => {
  // #region Data
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [confirmDialogVisible, setConfirmDialogVisible] = useState(false);
  const [historyVisible, setHistoryVisible] = useState(false);
  const [requestControl, setRequestControl] = useState(null);

  const {
    handleSubmit,
    control,
    trigger,
    reset,
    setValue,
    watch,
    formState: { errors, dirtyFields },
  } = useForm({ ...formOptions, mode: 'onChange' });

  const { mutate: mutateDownloadFile } = useMutation(downloadFile);
  const { mutate: mutateRequestUpdate, isLoading: isRequestUpdateLoading } =
    useMutation(requestUpdate);
  const { mutate: mutateUpdateUpdateRequest, isLoading: isUpdateUpdateRequestLoading } =
    useMutation(updateUpdateRequest);

  const {
    data: updateRequestsData,
    isLoading: isUpdateRequestsLoading,
    refetch: refetchUpdateRequests,
  } = useQuery(
    ['projectProposalReviseLogs', requestControl?.value?.reference, 'REQUEST_UPDATE'],
    () =>
      getReviseLogs({
        projectProposalId: requestControl?.value?.reference,
        types: ['REQUEST_UPDATE'],
      }),
    {
      enabled: !!requestControl?.value?.reference,
      staleTime: 0,
    }
  );

  const oldCommentFileUrls = watch('oldCommentFileUrls');

  const { openedRequest, mode, mutate } = useMemo(() => {
    const request =
      updateRequestsData?.data?.length > 0
        ? updateRequestsData?.data?.find((item) => item?.isOpened)
        : null;

    if (request) {
      setValue('comment', request?.comment ?? null);
      if (request?.commentFileUrls?.length > 0) {
        setValue('oldCommentFileUrls', request?.commentFileUrls ?? []);
      }
    }

    const currMode = request ? 'updateUpdateRequest' : 'requestUpdate';

    const mutates = {
      requestUpdate: mutateRequestUpdate,
      updateUpdateRequest: mutateUpdateUpdateRequest,
    };
    const currMutate = mutates[currMode];

    return { mode: currMode, mutate: currMutate, openedRequest: request };
  }, [updateRequestsData, mutateRequestUpdate, mutateUpdateUpdateRequest, setValue]);

  // #endregion

  // #region Event
  useImperativeHandle(
    ref,
    () => ({
      open: (_requestControl) => {
        setRequestControl(_requestControl);
        setVisible(true);
        refetchUpdateRequests();
      },
    }),
    []
  );

  const handleDownloadFile = (path, projectProposalId) => {
    mutateDownloadFile(
      {
        projectProposalId,
        path,
      },
      {
        onSuccess: async (res) => {
          if (res?.data) {
            const file = new Blob([res.data]);

            const link = document.createElement('a');
            link.href = URL.createObjectURL(file);
            link.download = formatFileName(path);

            link.click();
          }
        },
      }
    );
  };

  const onSubmit = (data) => {
    const formData = new FormData();

    if (mode === 'updateUpdateRequest') {
      formData.append('shouldResendRequest', 'true');
      formData.append('isOpened', 'true');
    }

    if (data?.commentFiles?.length > 0) {
      data?.commentFiles?.forEach((file) => {
        formData.append('commentFiles', file);
      });
    }
    if (data?.comment) {
      formData.append('comment', data?.comment);
    }

    if (openedRequest?.id) {
      formData.append('id', openedRequest?.id);
    }
    if (requestControl?.value?.reference) {
      formData.append('projectProposalId', requestControl?.value?.reference);
    }
    formData.append('oldCommentFileUrls', JSON.stringify(oldCommentFileUrls ?? []));

    mutate(formData, {
      onSuccess: () => {
        toast(TOAST.SUCCESS, t(`projectProposal.${mode}.success`));
        reset();
        refetchUpdateRequests();
        setConfirmDialogVisible(false);
        setVisible(false);
      },
      onError: (error) => {
        toast(TOAST.ERROR, mapError(t, error));
      },
    });
  };

  const checkFormValid = async () => {
    const isValid = await trigger();
    if (!isValid) {
      toast(TOAST.ERROR, t('errorMessage.validationErrorMessage'));
    } else {
      setConfirmDialogVisible(true);
    }
  };
  // #endregion

  // #region Format history
  const formatHeader = (value, rowIndex) => {
    const deadline = getMaxDate(
      requestControl?.value?.organizationPrequalificationDeadline,
      requestControl?.value?.program?.organizationPrequalificationDeadline
    );

    let statusJsx = null;
    if (!value?.isOpened) {
      statusJsx = (
        <span className="font-medium text-green-500 text-base font-italic">
          {t('projectProposal.requestUpdate.ownerAddedExplanation')}
        </span>
      );
    } else if (value?.isOpened && checkOverdueDatetime(deadline)) {
      statusJsx = (
        <span className="font-medium text-red-500 text-base font-italic">
          {t('projectProposal.requestUpdate.overdueDeadline')}
        </span>
      );
    } else {
      statusJsx = (
        <span className="font-medium text-red-500 text-base font-italic">
          {t('projectProposal.requestUpdate.deadline')}:{' '}
          {deadline
            ? new Date(deadline).toLocaleString('en-GB', { timeZone: TIMEZONE_VN })
            : t('projectProposal.requestUpdate.noDeadline')}
        </span>
      );
    }

    return (
      <div className="flex flex-column text-lg font-bold gap-1">
        <span>
          {rowIndex + 1}.{' '}
          {new Date(value?.createdAt).toLocaleString('en-GB', { timeZone: TIMEZONE_VN })}
        </span>
        <div>{statusJsx}</div>
      </div>
    );
  };
  const formatComment = (value) => (
    <div className="mb-2">
      <span className="font-semibold">{t('projectProposal.requestUpdate.comment')}:</span>
      <p className="my-2 text-justify" style={{ whiteSpace: 'pre-wrap' }}>
        {value?.comment}
      </p>
    </div>
  );
  const formatCommentFileUrls = (value) => (
    <div className="mb-2">
      <span className="mr-2 font-semibold">{t('projectProposal.requestUpdate.commentFile')}:</span>
      {value?.commentFileUrls?.length > 0 && (
        <ul className="list-disc">
          {value?.commentFileUrls?.map((item, index) => (
            <li className="-ml-4" key={item?.id ?? index}>
              <span
                className="p-button p-button-text p-button-sm p-0"
                onClick={() => handleDownloadFile(item?.name)}
              >
                <span className="text-blue-500 underline font-italic text-base">
                  {formatFileName(item?.name)}
                </span>
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
  // #endregion

  return (
    <Dialog
      header={
        historyVisible ? (
          <div className="flex justify-content-start gap-1 -ml-3">
            <Button
              icon="pi pi-arrow-left"
              onClick={() => setHistoryVisible(false)}
              className="p-button-text m-0 p-0 text-900"
            />
            {t('projectProposal.requestUpdate.history')}
          </div>
        ) : (
          t('projectProposal.requestUpdate.title')
        )
      }
      draggable={false}
      visible={visible}
      style={{ width: '700px' }}
      onHide={() => {
        reset();
        // hide the dialog first, then reset the historyVisible state shortly after
        // to avoid slight glitch when closing the dialog
        setVisible(false);
        setTimeout(() => {
          setHistoryVisible(false);
        }, 100);
      }}
      closable={!isRequestUpdateLoading && !isUpdateUpdateRequestLoading}
    >
      <div className="relative">
        {historyVisible ? (
          <Accordion
            multiple
            activeIndex={updateRequestsData?.data
              ?.map((item, index) => (item?.isOpened ? index : null))
              ?.filter((index) => index !== null)}
          >
            {updateRequestsData?.data?.map((rowData, rowIndex) => (
              <AccordionTab key={rowData.id} header={formatHeader(rowData, rowIndex)}>
                {formatComment(rowData)}
                {formatCommentFileUrls(rowData)}
              </AccordionTab>
            ))}
          </Accordion>
        ) : (
          <>
            <span
              className="text-blue-500 underline font-italic font-normal text-base text-left inline-block -mt-5 mb-2 cursor-pointer"
              onClick={() => setHistoryVisible(true)}
            >
              {t('projectProposal.requestUpdate.viewHistory')}
            </span>
            <form
              onSubmit={handleSubmit(onSubmit)}
              className="p-fluid flex flex-column"
              autoComplete="off"
            >
              <div className="grid">
                <div className="col-12 mb-2">
                  <TextareaInput
                    label={t('projectProposal.requestUpdate.comment')}
                    name="comment"
                    control={control}
                    errors={errors}
                    row={5}
                    isRequired
                    disabled={isRequestUpdateLoading || isUpdateUpdateRequestLoading}
                  />
                </div>

                <div className="col-12 mb-2">
                  <label htmlFor="commentFiles">
                    {t('projectProposal.requestUpdate.commentFile')}{' '}
                  </label>
                  {oldCommentFileUrls?.map((fileUrl, index) => (
                    <div
                      key={fileUrl?.id ?? index}
                      className="flex justify-content-between m-1 p-1"
                    >
                      <span
                        className="text-blue-500 underline font-italic text-base"
                        onClick={() =>
                          handleDownloadFile(fileUrl?.name, requestControl?.value?.reference)
                        }
                      >
                        {formatFileName(fileUrl?.name)}
                      </span>
                      <i
                        className="pi pi-times text-red-500 text-xl ml-2"
                        onClick={() => {
                          const fileUrls = [...oldCommentFileUrls];
                          fileUrls.splice(index, 1);
                          setValue('oldCommentFileUrls', fileUrls, { shouldDirty: true });
                        }}
                      />
                    </div>
                  ))}
                  <FileInput
                    name="commentFiles"
                    control={control}
                    errors={errors}
                    accept=".pdf,.docx,.doc,.xlsx,.xls,.csv"
                    acceptOnly="pdfWordExcelOnly"
                    multiple
                    disabled={isRequestUpdateLoading || isUpdateUpdateRequestLoading}
                  />
                </div>
              </div>

              <div className="grid mt-2">
                <div className="col-6">
                  <Button
                    label={t('formLayout.action.cancel')}
                    type="button"
                    severity="danger"
                    onClick={() => {
                      reset();
                      setVisible(false);
                    }}
                    disabled={isRequestUpdateLoading || isUpdateUpdateRequestLoading}
                  />
                </div>
                <div className="col-6">
                  <Button
                    label={
                      mode === 'updateUpdateRequest'
                        ? t('projectProposal.updateExplanationRequest.updateAndResend')
                        : t('projectProposal.requestExplanation.sendRequest')
                    }
                    className="bg-cyan-400 border-cyan-400 hover:bg-cyan-500"
                    type="button"
                    onClick={checkFormValid}
                    disabled={
                      isRequestUpdateLoading ||
                      isUpdateUpdateRequestLoading ||
                      !(Object.keys(dirtyFields)?.length > 0)
                    }
                  />
                </div>
              </div>

              {/*
                confirm dialog: use this instead of using .requestSubmit() of form,
                avoid browser compatibility error
              */}
              <div
                className={` ${
                  confirmDialogVisible ? '' : 'hidden '
                } p-dialog-mask p-dialog-center p-component-overlay p-component-overlay-enter p-dialog-visible p-dialog-resizable p-dialog-draggable`}
              >
                <div
                  className="p-dialog p-component p-dialog-default p-dialog-enter-done"
                  role="dialog"
                  aria-modal="true"
                  style={{ width: '450px' }}
                >
                  {(isRequestUpdateLoading || isUpdateUpdateRequestLoading) && <Loading />}

                  <div className="p-dialog-header">
                    <div className="p-dialog-title">
                      <p className="text-blue-500">{t('projectProposal.confirmationTitle')}</p>
                    </div>
                    <div className="p-dialog-header-icons">
                      <Button
                        type="button"
                        className="p-dialog-header-icon p-dialog-header-close p-link"
                        aria-label="Close"
                        onClick={() => setConfirmDialogVisible(false)}
                        disabled={isRequestUpdateLoading || isUpdateUpdateRequestLoading}
                      >
                        <span
                          className="p-dialog-header-close-icon pi pi-times"
                          aria-hidden="true"
                        />
                        <span role="presentation" className="p-ink" />
                      </Button>
                    </div>
                  </div>
                  <div className="p-dialog-content">
                    <p className="text-lg font-bold">{t(`projectProposal.${mode}.infoMessage`)}</p>
                    <p className="text-lg">{t(`projectProposal.${mode}.confirmationMessage`)}</p>
                  </div>
                  <div className="p-dialog-footer">
                    <div>
                      <Button
                        label={t('projectProposal.cancel')}
                        className="w-7rem"
                        severity="danger"
                        onClick={() => setConfirmDialogVisible(false)}
                        autoFocus
                        disabled={isRequestUpdateLoading || isUpdateUpdateRequestLoading}
                        type="button"
                      />
                      <Button
                        label={t('projectProposal.ok')}
                        className="w-7rem"
                        severity="info"
                        disabled={isRequestUpdateLoading || isUpdateUpdateRequestLoading}
                        type="submit"
                      />
                    </div>
                  </div>
                  <span className="p-resizable-handle" style={{ zIndex: 90 }} />
                </div>
              </div>
            </form>
          </>
        )}
      </div>

      {isUpdateRequestsLoading && <Loading />}
    </Dialog>
  );
});

export default RequestUpdateForm;

RequestUpdateForm.propTypes = {};
